import React, { useState, useCallback } from 'react';
import { Title } from 'react-admin';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';

import {
  uploadCatalogFieldMappings,
  type UploadCatalogFieldsResult,
} from '@src/apis/reware/catalog/uploadCatalogFieldMappings';
import { downloadCatalogFieldMappings } from '@src/apis/reware/catalog/downloadCatalogFieldMappings';
import { getCurrentPartner } from '@src/providers/dataProvider/shared/partner';

const CatalogFieldsMappingUpload = () => {
  const partner = getCurrentPartner();
  const partnerCode = partner?.code ?? '';

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<{
    type: 'success' | 'error' | 'info';
    message: string;
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Preview / confirmation dialog state
  const [previewResult, setPreviewResult] = useState<UploadCatalogFieldsResult | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.tsv')) {
      setUploadStatus({ type: 'error', message: 'Please select a .tsv file.' });
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setUploadStatus(null);
    // Reset input value so re-selecting the same file triggers onChange
    event.target.value = '';
  };

  const handleDrop = useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const file = event.dataTransfer.files?.[0];
      if (!file) return;

      if (!file.name.endsWith('.tsv')) {
        setUploadStatus({ type: 'error', message: 'Please select a .tsv file.' });
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setUploadStatus(null);
    },
    [],
  );

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleUpload = async () => {
    if (!selectedFile || !partnerCode) return;

    setIsUploading(true);
    setUploadStatus(null);

    try {
      // First call with preview=true to get dry-run report
      const result = await uploadCatalogFieldMappings(partnerCode, selectedFile, true);
      if (result.success) {
        setPreviewResult(result);
        setShowConfirmDialog(true);
      } else {
        setUploadStatus({
          type: 'error',
          message: result.error || 'Preview failed.',
        });
      }
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Preview failed.',
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmUpload = async () => {
    if (!selectedFile || !partnerCode) return;

    setShowConfirmDialog(false);
    setIsUploading(true);
    setUploadStatus(null);

    try {
      const result = await uploadCatalogFieldMappings(partnerCode, selectedFile, false);
      if (result.success) {
        setUploadStatus({
          type: 'success',
          message: `Successfully uploaded: ${result.added} added, ${result.updated} updated, ${result.deleted} deleted, ${result.unchanged} unchanged.`,
        });
        setSelectedFile(null);
      } else {
        setUploadStatus({
          type: 'error',
          message: result.error || 'Upload failed.',
        });
      }
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Upload failed.',
      });
    } finally {
      setIsUploading(false);
      setPreviewResult(null);
    }
  };

  const handleCancelUpload = () => {
    setShowConfirmDialog(false);
    setPreviewResult(null);
  };

  const handleDownload = async () => {
    if (!partnerCode) return;

    setIsDownloading(true);
    try {
      await downloadCatalogFieldMappings(partnerCode);
    } catch (error) {
      setUploadStatus({
        type: 'error',
        message: error instanceof Error ? error.message : 'Download failed.',
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Title title="Catalog Fields Mapping Upload" />

      <Typography variant="h5" component="h1" gutterBottom>
        Catalog Fields Mapping Upload
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Bulk upload display type mappings for catalog fields.
      </Typography>

      {/* Download Current Mappings */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : `Download Current Mappings for ${partner?.name ?? 'Partner'}`}
          </Button>
        </CardContent>
      </Card>

      {/* File Upload Area */}
      <Card>
        <CardContent>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            sx={{
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
              p: 4,
              textAlign: 'center',
              bgcolor: 'background.default',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
            }}
          >
            <UploadIcon
              sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }}
            />
            <Typography variant="body1" gutterBottom>
              Drag and drop a TSV file here
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              gutterBottom
            >
              or
            </Typography>
            <Button
              variant="contained"
              component="label"
              sx={{ mt: 1 }}
            >
              Choose File
              <input
                type="file"
                hidden
                accept=".tsv"
                onChange={handleFileSelect}
              />
            </Button>
          </Box>

          {/* Selected file info + upload button */}
          {selectedFile && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography variant="body2">
                Selected:
                {' '}
                {selectedFile.name}
              </Typography>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={isUploading}
              >
                {isUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </Box>
          )}

          {/* Status message */}
          {uploadStatus && (
            <Typography
              variant="body2"
              sx={{
                mt: 2,
                color:
                  uploadStatus.type === 'error' ?
                    'error.main' :
                    uploadStatus.type === 'success' ?
                      'success.main' :
                      'info.main',
              }}
            >
              {uploadStatus.message}
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Upload Confirmation Dialog */}
      <Dialog
        open={showConfirmDialog}
        onClose={handleCancelUpload}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Upload</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The following changes will be applied:
          </Typography>
          {previewResult && (
            <Box component="ul" sx={{ m: 0, pl: 3 }}>
              <li>
                <Typography variant="body2">
                  <strong>{previewResult.added}</strong>
                  {' '}
                  mapping(s) added
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>{previewResult.updated}</strong>
                  {' '}
                  mapping(s) updated
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>{previewResult.deleted}</strong>
                  {' '}
                  mapping(s) deleted
                </Typography>
              </li>
              <li>
                <Typography variant="body2">
                  <strong>{previewResult.unchanged}</strong>
                  {' '}
                  mapping(s) unchanged
                </Typography>
              </li>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelUpload}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmUpload}
          >
            Confirm Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CatalogFieldsMappingUpload;
